import React, { useState } from "react";
import { Download, LoaderCircle } from "lucide-react";
import { LOGO_URL } from "@/siteData";

const loadImage = (src) => new Promise((resolve) => {
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);
  img.src = src;
});

const drawCover = (ctx, img, x, y, size) => {
  const side = Math.min(img.naturalWidth, img.naturalHeight);
  ctx.drawImage(img, (img.naturalWidth - side) / 2, (img.naturalHeight - side) / 2, side, side, x, y, size, size);
};

async function composeMenuCard(cocktails) {
  await document.fonts.ready;
  const shown = cocktails.slice(0, 8);
  const extra = cocktails.length - shown.length;
  const W = 1080;
  const headerH = 320;
  const rowH = 196;
  const H = headerH + shown.length * rowH + (extra > 0 ? 78 : 0) + 180;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#0b0908";
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = "rgba(212,155,75,.5)";
  ctx.lineWidth = 2;
  ctx.strokeRect(26, 26, W - 52, H - 52);
  ctx.strokeStyle = "rgba(212,155,75,.18)";
  ctx.strokeRect(36, 36, W - 72, H - 72);

  const [logo, ...thumbs] = await Promise.all([loadImage(LOGO_URL), ...shown.map((item) => loadImage(item.image))]);
  if (logo) {
    const lw = 96 * (logo.naturalWidth / logo.naturalHeight);
    ctx.drawImage(logo, (W - lw) / 2, 62, lw, 96);
  }
  ctx.textAlign = "center";
  ctx.fillStyle = "#d49b4b";
  ctx.font = "700 20px 'DM Sans', Arial";
  ctx.fillText("P O P   S O C I A L   E V E N T S", W / 2, logo ? 198 : 120);
  ctx.fillStyle = "#faf7f2";
  ctx.font = "500 68px 'Cormorant Garamond', Georgia, serif";
  ctx.fillText("The Evening’s Menu", W / 2, logo ? 268 : 190);
  ctx.strokeStyle = "rgba(212,155,75,.4)";
  ctx.beginPath();
  ctx.moveTo(120, headerH - 18);
  ctx.lineTo(W - 120, headerH - 18);
  ctx.stroke();

  ctx.textAlign = "left";
  shown.forEach((cocktail, index) => {
    const y = headerH + index * rowH;
    const thumb = thumbs[index];
    if (thumb) drawCover(ctx, thumb, 76, y + 24, 140);
    ctx.strokeStyle = "rgba(212,155,75,.35)";
    ctx.strokeRect(76, y + 24, 140, 140);
    ctx.fillStyle = "#d49b4b";
    ctx.font = "700 15px 'DM Sans', Arial";
    ctx.fillText(cocktail.categoryLabel.toUpperCase(), 252, y + 52);
    ctx.fillStyle = "#faf7f2";
    ctx.font = "500 44px 'Cormorant Garamond', Georgia, serif";
    ctx.fillText(cocktail.name, 250, y + 102);
    ctx.fillStyle = "#9c8f80";
    ctx.font = "400 19px 'DM Sans', Arial";
    let line = cocktail.ingredients.slice(0, 4).join("  ·  ");
    while (ctx.measureText(line).width > W - 330) line = line.slice(0, -8).trimEnd() + "…";
    ctx.fillText(line, 252, y + 140);
    if (index < shown.length - 1) {
      ctx.strokeStyle = "rgba(212,155,75,.14)";
      ctx.beginPath();
      ctx.moveTo(76, y + rowH - 4);
      ctx.lineTo(W - 76, y + rowH - 4);
      ctx.stroke();
    }
  });

  ctx.textAlign = "center";
  if (extra > 0) {
    ctx.fillStyle = "#9c8f80";
    ctx.font = "italic 400 24px 'Cormorant Garamond', Georgia, serif";
    ctx.fillText(`+ ${extra} more pour${extra > 1 ? "s" : ""} on the evening’s list`, W / 2, headerH + shown.length * rowH + 44);
  }
  const footY = H - 118;
  ctx.strokeStyle = "rgba(212,155,75,.4)";
  ctx.beginPath();
  ctx.moveTo(120, footY - 34);
  ctx.lineTo(W - 120, footY - 34);
  ctx.stroke();
  ctx.fillStyle = "#faf7f2";
  ctx.font = "italic 500 28px 'Cormorant Garamond', Georgia, serif";
  ctx.fillText("Crafted & poured by Pop Social Events", W / 2, footY + 8);
  ctx.fillStyle = "#9c8f80";
  ctx.font = "400 18px 'DM Sans', Arial";
  ctx.fillText("www.popsocialevents.com  ·  carlos@popsocialevents.com  ·  Miami · Broward · Palm Beach", W / 2, footY + 46);
  return canvas;
}

export { composeMenuCard };

export const MenuCardButton = ({ cocktails }) => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    if (!cocktails.length || busy) return;
    setBusy(true);
    try {
      const canvas = await composeMenuCard(cocktails);
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "pop-social-event-menu.png";
        link.click();
        window.setTimeout(() => URL.revokeObjectURL(url), 4000);
      }, "image/png");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" className="text-button menu-card-button" onClick={download} disabled={!cocktails.length || busy} data-testid="menu-card-download-button" title={cocktails.length ? "Download a shareable menu card of your selected pours" : "Add cocktails to your brief first"}>
      {busy ? <LoaderCircle className="spin" size={15} /> : <Download size={15} />}
      {busy ? "Composing card…" : "Download menu card"}
    </button>
  );
};
